import { useState } from 'react';
import { checkEligibility } from '../services/api';
import './EligibilityModal.css';

// ── Helpers ──────────────────────────────────────────────────────────────────

// Claim.MD returns repeating elements as a single object when there's one, or an
// array when there are many. Normalize to an array.
const toArray = (v) => (v == null ? [] : Array.isArray(v) ? v : [v]);

// <input type="date"> value (yyyy-mm-dd) → Claim.MD's yyyymmdd.
const toYmd = (d) => (d ? String(d).slice(0, 10).replace(/-/g, '') : '');

// yyyymmdd (or a yyyymmdd-yyyymmdd range) → human readable.
function fmtYmd(s) {
  if (!s) return '—';
  const str = String(s);
  if (str.includes('-') && str.length > 8) {
    return str.split('-').map(fmtYmd).join(' – ');
  }
  const m = str.match(/^(\d{4})(\d{2})(\d{2})$/);
  if (!m) return str;
  const dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Split a stored "Last, First" (or "First Last") subscriber name into parts.
function splitName(name) {
  if (!name) return { first: '', last: '' };
  if (name.includes(',')) {
    const [last, first] = name.split(',');
    return { first: (first || '').trim(), last: (last || '').trim() };
  }
  const parts = name.trim().split(/\s+/);
  return { first: parts[0] || '', last: parts.slice(1).join(' ') };
}

const NETWORK_LABELS = { Y: 'In-Network', N: 'Out-of-Network', W: 'Both' };
const UNLIMITED = 999999;

function fmtBenefitValue(b) {
  if (b.benefit_percent != null && b.benefit_percent !== '') return `${b.benefit_percent}%`;
  if (b.benefit_amount != null && b.benefit_amount !== '') {
    const amt = parseFloat(b.benefit_amount);
    if (Number.isNaN(amt)) return b.benefit_amount;
    if (amt >= UNLIMITED) return 'Unlimited';
    return '$' + amt.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  if (b.benefit_qnty != null && b.benefit_qnty !== '') return `${b.benefit_qnty} visits`;
  return '—';
}

// Pull a single plan-level dollar figure (e.g. Deductible / Out-of-Pocket) for a
// given coverage code + member level, preferring in-network calendar-year values.
function pickAmount(benefits, coverageCode, levelCode) {
  const matches = benefits.filter(
    (b) => b.benefit_code === '30' &&
      b.benefit_coverage_code === coverageCode &&
      b.benefit_level_code === levelCode &&
      b.benefit_amount != null && b.benefit_amount !== ''
  );
  if (matches.length === 0) return null;
  const remaining = matches.find((b) => /remaining/i.test(b.benefit_period_description || '') && b.inplan_network !== 'N');
  const calendar  = matches.find((b) => /calendar/i.test(b.benefit_period_description || '') && b.inplan_network !== 'N');
  const chosen = remaining || calendar || matches[0];
  const amt = parseFloat(chosen.benefit_amount);
  return {
    value: amt >= UNLIMITED ? 'Unlimited' : '$' + amt.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    period: chosen.benefit_period_description || null,
  };
}

export default function EligibilityModal({ patient, insurance, facility, onClose }) {
  const isSelf = !insurance?.subscriber_relation || insurance.subscriber_relation === 'Self';
  const insuredName = isSelf
    ? { first: patient?.first_name || '', last: patient?.last_name || '' }
    : splitName(insurance?.subscriber_name);

  const [form, setForm] = useState({
    payerid:    insurance?.payer_code || '',
    insLast:    insuredName.last,
    insFirst:   insuredName.first,
    insNumber:  insurance?.policy_number || '',
    insDob:     (isSelf ? patient?.date_of_birth : insurance?.subscriber_dob) ? String(isSelf ? patient.date_of_birth : insurance.subscriber_dob).slice(0, 10) : '',
    insSex:     (isSelf ? patient?.sex : insurance?.subscriber_sex) || '',
    fdos:       new Date().toISOString().slice(0, 10),
    serviceCode: '30',
    provNpi:    facility?.npi || '',
    provTaxid:  (facility?.tax_id || '').replace(/\D/g, ''),
    provName:   facility?.name || '',
  });

  const [phase, setPhase]     = useState('form'); // 'form' | 'results'
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [result, setResult]   = useState(null);

  const upd = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleCheck = async () => {
    // Minimal client-side guard mirroring Claim.MD's required fields.
    const missing = [];
    if (!form.payerid)   missing.push('Payer ID');
    if (!form.insLast)   missing.push('Insured last name');
    if (!form.insFirst)  missing.push('Insured first name');
    if (!form.fdos)      missing.push('Service date');
    if (!form.provNpi)   missing.push('Provider NPI');
    if (!form.provTaxid) missing.push('Provider Tax ID');
    if (missing.length > 0) {
      setError('Missing required field(s): ' + missing.join(', '));
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const params = {
        payerid:    form.payerid.trim(),
        ins_name_l: form.insLast.trim(),
        ins_name_f: form.insFirst.trim(),
        ins_number: form.insNumber.trim() || undefined,
        ins_dob:    toYmd(form.insDob) || undefined,
        ins_sex:    form.insSex || undefined,
        pat_rel:    isSelf ? '18' : 'G8',
        fdos:       toYmd(form.fdos),
        service_code: form.serviceCode || undefined,
        prov_npi:   form.provNpi.replace(/\D/g, ''),
        prov_taxid: form.provTaxid.replace(/\D/g, ''),
        prov_name_l: form.provName.trim() || undefined,
      };
      if (!isSelf) {
        params.pat_name_l = patient?.last_name || undefined;
        params.pat_name_f = patient?.first_name || undefined;
        params.pat_dob    = toYmd(patient?.date_of_birth) || undefined;
        params.pat_sex    = patient?.sex || undefined;
      }

      const res = await checkEligibility(params);
      const data = res?.eligibility ?? res;
      let elig = data?.elig ?? data?.result?.elig ?? data;
      if (Array.isArray(elig)) elig = elig[0];

      // Payer/EDI-level error inside an otherwise-200 response.
      const errObj = toArray(elig?.error ?? data?.error ?? data?.result?.error)[0];
      if (errObj && (errObj.error_mesg || errObj.error_code)) {
        setError(`Payer returned an error: ${errObj.error_mesg || errObj.error_code}`);
        setLoading(false);
        return;
      }

      setResult(elig || {});
      setPhase('results');
    } catch (err) {
      // The server wraps clearinghouse failures as 502 with a message + the raw
      // Claim.MD body in `details`. Prefer the payer's own message (e.g. a bad
      // NPI or member ID) over the internal "/services/eligdata/ failed" wrapper.
      const body = err?.response?.data;
      const detailErr = body?.details?.error;
      const payerMsg = Array.isArray(detailErr)
        ? detailErr.map((e) => e?.error_mesg).filter(Boolean).join('; ')
        : detailErr?.error_mesg;
      setError(
        payerMsg
        || body?.message
        || body?.error
        || err.message
        || 'Eligibility check failed. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  // ── Results derivation ─────────────────────────────────────────────────────
  const benefits = toArray(result?.benefit);
  const planBenefit = benefits.find((b) => b.benefit_code === '30' && b.benefit_coverage_code === '1');
  const inactive    = benefits.find((b) => b.benefit_code === '30' && b.benefit_coverage_code === '6');
  const coverageStatus = planBenefit ? 'active' : inactive ? 'inactive' : 'unknown';

  const dedInd  = pickAmount(benefits, 'C', 'IND');
  const dedFam  = pickAmount(benefits, 'C', 'FAM');
  const oopInd  = pickAmount(benefits, 'G', 'IND');
  const oopFam  = pickAmount(benefits, 'G', 'FAM');
  const summaryCards = [
    { label: 'Deductible (Individual)',     data: dedInd },
    { label: 'Deductible (Family)',         data: dedFam },
    { label: 'Out-of-Pocket (Individual)',  data: oopInd },
    { label: 'Out-of-Pocket (Family)',      data: oopFam },
  ].filter((c) => c.data);

  const [benefitFilter, setBenefitFilter] = useState('');
  const filteredBenefits = benefits.filter((b) => {
    if (!benefitFilter.trim()) return true;
    const q = benefitFilter.toLowerCase();
    return (b.benefit_description || '').toLowerCase().includes(q) ||
      (b.benefit_coverage_description || '').toLowerCase().includes(q);
  });

  return (
    <div className="elig-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget && !loading) onClose(); }}>
      <div className="elig-modal">

        {/* Header */}
        <div className="elig-header">
          <div className="elig-header-left">
            <div className="elig-header-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 12l2 2 4-4" /><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            </div>
            <div>
              <h2 className="elig-title">Eligibility &amp; Benefits</h2>
              <p className="elig-subtitle">
                {patient?.first_name} {patient?.last_name} &middot; {insurance?.insurance_provider || 'Insurance'}
              </p>
            </div>
          </div>
          <button className="elig-close" onClick={onClose} disabled={loading}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="elig-body">
          {phase === 'form' ? (
            <>
              <p className="elig-hint">
                Confirm the request details below, then run a real-time eligibility check
                with the payer through Claim.MD.
              </p>

              <div className="elig-section-label">Payer &amp; Service</div>
              <div className="elig-form-grid">
                <div className="elig-field">
                  <label className="elig-label">Payer ID <span className="elig-req">*</span></label>
                  <input className="elig-input" value={form.payerid} onChange={upd('payerid')} placeholder="Claim.MD payer ID" />
                </div>
                <div className="elig-field">
                  <label className="elig-label">Service Date <span className="elig-req">*</span></label>
                  <input className="elig-input" type="date" value={form.fdos} onChange={upd('fdos')} />
                </div>
                <div className="elig-field">
                  <label className="elig-label">Benefit Type</label>
                  <select className="elig-input" value={form.serviceCode} onChange={upd('serviceCode')}>
                    <option value="30">Health Benefit Plan Coverage</option>
                    <option value="1">Medical Care</option>
                    <option value="98">Physician Visit - Office</option>
                    <option value="48">Hospital - Inpatient</option>
                    <option value="50">Hospital - Outpatient</option>
                    <option value="86">Emergency Services</option>
                    <option value="88">Pharmacy</option>
                    <option value="MH">Mental Health</option>
                    <option value="PT">Physical Therapy</option>
                    <option value="UC">Urgent Care</option>
                  </select>
                </div>
              </div>

              <div className="elig-section-label">
                Insured {isSelf ? '(Patient — Self)' : '(Subscriber)'}
              </div>
              <div className="elig-form-grid">
                <div className="elig-field">
                  <label className="elig-label">Last Name <span className="elig-req">*</span></label>
                  <input className="elig-input" value={form.insLast} onChange={upd('insLast')} />
                </div>
                <div className="elig-field">
                  <label className="elig-label">First Name <span className="elig-req">*</span></label>
                  <input className="elig-input" value={form.insFirst} onChange={upd('insFirst')} />
                </div>
                <div className="elig-field">
                  <label className="elig-label">Member / Policy ID</label>
                  <input className="elig-input" value={form.insNumber} onChange={upd('insNumber')} />
                </div>
                <div className="elig-field">
                  <label className="elig-label">Date of Birth</label>
                  <input className="elig-input" type="date" value={form.insDob} onChange={upd('insDob')} />
                </div>
                <div className="elig-field">
                  <label className="elig-label">Sex</label>
                  <select className="elig-input" value={form.insSex} onChange={upd('insSex')}>
                    <option value="">—</option>
                    <option value="M">Male</option>
                    <option value="F">Female</option>
                  </select>
                </div>
              </div>
              {!isSelf && (
                <p className="elig-note">
                  Checking as a dependent ({insurance?.subscriber_relation}); the patient
                  ({patient?.first_name} {patient?.last_name}) is sent as the dependent and
                  the subscriber above as the insured.
                </p>
              )}

              <div className="elig-section-label">Provider (Billing)</div>
              <div className="elig-form-grid">
                <div className="elig-field elig-span-2">
                  <label className="elig-label">Provider / Org Name</label>
                  <input className="elig-input" value={form.provName} onChange={upd('provName')} />
                </div>
                <div className="elig-field">
                  <label className="elig-label">NPI <span className="elig-req">*</span></label>
                  <input className="elig-input" value={form.provNpi} onChange={upd('provNpi')} placeholder="10-digit NPI" />
                </div>
                <div className="elig-field">
                  <label className="elig-label">Tax ID <span className="elig-req">*</span></label>
                  <input className="elig-input" value={form.provTaxid} onChange={upd('provTaxid')} />
                </div>
              </div>

              {error && <div className="elig-error">{error}</div>}
            </>
          ) : (
            <>
              {/* Status banner */}
              <div className={`elig-status elig-status-${coverageStatus}`}>
                <div className="elig-status-icon">
                  {coverageStatus === 'active' ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                  ) : coverageStatus === 'inactive' ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                  )}
                </div>
                <div>
                  <div className="elig-status-title">
                    {coverageStatus === 'active' ? 'Active Coverage' : coverageStatus === 'inactive' ? 'Inactive Coverage' : 'Coverage Status Unknown'}
                  </div>
                  <div className="elig-status-sub">
                    {planBenefit?.insurance_plan || result?.plan_name || insurance?.insurance_provider || ''}
                    {planBenefit?.insurance_type_description ? ` · ${planBenefit.insurance_type_description}` : ''}
                  </div>
                </div>
              </div>

              {/* Member / plan info */}
              <div className="elig-info-grid">
                {[
                  ['Member', `${result?.ins_name_f || ''} ${result?.ins_name_l || ''}`.trim()],
                  ['Member ID', result?.ins_number],
                  ['Group #', result?.group_number],
                  ['Plan', result?.plan_name || result?.plan_number],
                  ['Plan Dates', (result?.plan_begin_date || result?.plan_end_date) ? `${fmtYmd(result?.plan_begin_date)}${result?.plan_end_date ? ' – ' + fmtYmd(result.plan_end_date) : ''}` : null],
                  ['Result Date', fmtYmd(result?.elig_result_date)],
                ].filter(([, v]) => v && v !== '—').map(([label, value]) => (
                  <div key={label} className="elig-info-item">
                    <span className="elig-info-label">{label}</span>
                    <span className="elig-info-value">{value}</span>
                  </div>
                ))}
              </div>

              {/* Cost-sharing summary cards */}
              {summaryCards.length > 0 && (
                <>
                  <div className="elig-section-label">Cost Sharing</div>
                  <div className="elig-card-row">
                    {summaryCards.map((c) => (
                      <div key={c.label} className="elig-card">
                        <span className="elig-card-label">{c.label}</span>
                        <span className="elig-card-value">{c.data.value}</span>
                        {c.data.period && <span className="elig-card-period">{c.data.period}</span>}
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Full benefit table */}
              <div className="elig-section-label elig-benefits-head">
                <span>Benefits ({benefits.length})</span>
                <input
                  className="elig-filter"
                  value={benefitFilter}
                  onChange={(e) => setBenefitFilter(e.target.value)}
                  placeholder="Filter by service or type…"
                />
              </div>
              {benefits.length === 0 ? (
                <div className="elig-empty">No benefit detail returned by the payer.</div>
              ) : (
                <div className="elig-table-wrap">
                  <table className="elig-table">
                    <thead>
                      <tr>
                        <th>Service</th><th>Type</th><th>Level</th><th>Network</th><th>Amount</th><th>Period</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredBenefits.map((b, i) => (
                        <tr key={i}>
                          <td>
                            {b.benefit_description || '—'}
                            {b.benefit_notes && <span className="elig-cell-note">{b.benefit_notes}</span>}
                          </td>
                          <td>{b.benefit_coverage_description || '—'}</td>
                          <td>{b.benefit_level_description || '—'}</td>
                          <td>{NETWORK_LABELS[b.inplan_network] || '—'}</td>
                          <td className="elig-td-amount">{fmtBenefitValue(b)}</td>
                          <td>{b.benefit_period_description || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="elig-footer">
          {phase === 'form' ? (
            <>
              <button className="elig-btn-cancel" onClick={onClose} disabled={loading}>Cancel</button>
              <button className="elig-btn-primary" onClick={handleCheck} disabled={loading}>
                {loading ? (<><span className="elig-spinner" />Checking…</>) : 'Check Eligibility'}
              </button>
            </>
          ) : (
            <>
              <button className="elig-btn-cancel" onClick={() => { setPhase('form'); setError(null); }}>
                ← New Check
              </button>
              <button className="elig-btn-primary" onClick={onClose}>Done</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
